<template>
  <BaseModal 
    :show="isOpen" 
    size="sm" 
    :title="modalTitle"
    :show-actions="true"
    :is-editing="isEditing"
    :loading="loading"
    @close="$emit('cancel')"
    @save="handleSave"
  >
    <!-- Name Field -->
    <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
      {{ $t('app.forms.name') || 'Name' }}
    </label>
    <input 
      v-model="localName" 
      type="text" 
      class="w-full rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      @keyup.enter="handleSave"
    >
      
    <!-- Description Field -->
    <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mt-4 mb-1">
      {{ $t('app.forms.description') || 'Description' }}
    </label>
    <textarea 
      v-model="localDescription" 
      rows="3" 
      class="w-full rounded-md border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
    />
      
    <!-- Visible in Kitchen Checkbox -->
    <div class="mt-4">
      <label class="flex items-center cursor-pointer">
        <input 
          v-model="localVisibleInKitchen" 
          type="checkbox"
          class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
        >
        <span class="ml-2 text-sm text-gray-700 dark:text-gray-200">
          {{ $t('app.forms.visible_in_kitchen') || 'Visible in Kitchen' }}
        </span>
      </label>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {{ $t('app.forms.visible_in_kitchen_help') || 'Uncheck to hide items from this category in the kitchen view (e.g., beverages)' }}
      </p>
    </div>
      
    <!-- Subscription Limit Alert -->
    <SubscriptionLimitAlert
      v-if="limitError"
      :message="limitError"
      :dismissible="false"
      class="mt-4"
    />
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SubscriptionLimitAlert from '@/components/subscription/SubscriptionLimitAlert.vue';
import BaseModal from '@/components/common/BaseModal.vue';

const props = defineProps<{
  isOpen: boolean;
  name: string;
  description: string;
  visibleInKitchen: boolean;
  isEditing: boolean;
  limitError: string;
  loading?: boolean;
}>();

const emit = defineEmits<{
  save: [];
  cancel: [];
  'update:name': [value: string];
  'update:description': [value: string];
  'update:visibleInKitchen': [value: boolean];
}>();

const { t } = useI18n();

const localName = computed({
  get: () => props.name,
  set: (value) => emit('update:name', value)
});

const localDescription = computed({
  get: () => props.description,
  set: (value) => emit('update:description', value)
});

const localVisibleInKitchen = computed({
  get: () => props.visibleInKitchen,
  set: (value) => emit('update:visibleInKitchen', value)
});

const modalTitle = computed(() => {
  const action = props.isEditing ? (t('app.actions.edit') || 'Edit') : (t('app.actions.add') || 'Add');
  const entity = t('app.forms.category') || 'Category';
  return `${action} ${entity}`;
});

function handleSave() {
  emit('save');
}
</script>
